import classNames from 'classnames';
import {
  FlexContainer,
  View,
  Txt,
  PartialWidthContainer,
  ErrorMessage,
  Button,
  Spinner,
  TxtTitle,
  ContentBlock,
  SelectButtons,
  Input,
  Icon,
  InputImages,
  SupportBlock,
  Checkbox,
  LabeledInput,
} from 'components';
import { companiesHouseUrl, picturesMaxSize } from 'config';
import { useFormInputState, useNavigationCallback } from 'hooks';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { proprietorTypes, proprietorTypesMap } from 'config/types';
import { useDataActions, usePublicAccount } from 'state';
import styles from './LandlordVerificationScreen.module.scss';
import { userTitleOptions } from 'config/userFields';
import { firstNameField, lastNameField, phoneField } from 'config/fieldsProps';

const proprietorTypesOptions = Object.keys(proprietorTypesMap).map((key) => ({
  key,
  value: proprietorTypesMap[key],
}));

/**
 * LandlordVerificationScreen
 *
 * @return {JSX.Element}
 */
const LandlordVerificationScreen = ({ className }) => {
  const publicAccount = usePublicAccount();
  const storageActions = useDataActions();
  const { gotToCallback } = useNavigationCallback();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataState, handleChange] = useFormInputState(() => ({
    ...publicAccount.data.proprietor,
    idImageData: '',
  }));

  const handleBack = () => gotToCallback(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await storageActions.general.sendVerificationRequest(dataState);
      gotToCallback(true);
    } catch (e) {
      setIsLoading(false);
      setError(e.message);
    }
  };

  return (
    <PartialWidthContainer
      className={classNames(
        styles.LandlordVerificationScreen,
        className,
        LandlordVerificationScreen.displayName
      )}
      column
      gap
      small
    >
      <FlexContainer>
        <Button onClick={handleBack}>
          <Icon s name="goback" /> Back
        </Button>
      </FlexContainer>
      <ContentBlock column>
        <View>
          <TxtTitle xxxx>Please fill up the verification form</TxtTitle>
        </View>
        <View>
          <SupportBlock />
        </View>
      </ContentBlock>
      <form onSubmit={handleSubmit}>
        <FlexContainer column gap>
          <FlexContainer column gapHalf>
            <FlexContainer column gapQuad>
              <View>
                <Txt xx bold>
                  Proprietor type
                </Txt>
              </View>
            </FlexContainer>
            <SelectButtons
              name="type"
              value={dataState.type}
              options={proprietorTypesOptions}
              onChange={handleChange}
              disabled={isLoading}
              fullWidth
              required
            />
          </FlexContainer>
          {dataState.type === proprietorTypes.company ? (
            <>
              <Txt regular>
                Company details are verified via companies house.
              </Txt>
              <a
                className={styles.ButtonLink}
                href={companiesHouseUrl}
                target="_blank"
                rel="noreferrer"
              >
                <Button flex reverse>
                  Go to Companies House
                </Button>
              </a>
              <FlexContainer column gapHalf>
                <FlexContainer column gapQuad>
                  <View>
                    <Txt xx bold>
                      Company name
                    </Txt>
                  </View>
                </FlexContainer>
                <Input
                  name="name"
                  value={dataState.name}
                  onChange={handleChange}
                  disabled={isLoading}
                  fullWidth
                  required
                />
              </FlexContainer>
              <FlexContainer column gapHalf>
                <FlexContainer column gapQuad>
                  <View>
                    <Txt xx bold>
                      Company number
                    </Txt>
                  </View>
                </FlexContainer>
                <Input
                  name="companyNumber"
                  type="text"
                  minLength="8"
                  maxLength="8"
                  placeholder="12287967"
                  pattern="\d+"
                  title="The company number contains 8 digits"
                  value={dataState.companyNumber}
                  onChange={handleChange}
                  disabled={isLoading}
                  fullWidth
                  required
                />
              </FlexContainer>
              <FlexContainer column gapHalf>
                <FlexContainer column gapQuad>
                  <View>
                    <Txt xx bold>
                      VAT number (optional)
                    </Txt>
                  </View>
                </FlexContainer>
                <Input
                  name="companyVATNumber"
                  type="number"
                  minLength="9"
                  maxLength="9"
                  placeholder="774839201"
                  value={dataState.companyVATNumber}
                  onChange={handleChange}
                  disabled={isLoading}
                  fullWidth
                />
              </FlexContainer>
              <ContentBlock black column noPadding>
                <Checkbox
                  name="isDirector"
                  onChange={handleChange}
                  checked={dataState.isDirector}
                  reverse
                  padding
                >
                  <FlexContainer column gapHalf>
                    <Txt bold>Are you the Director?</Txt>
                    <Txt regular>
                      Tap here to change your director status. If you are not
                      the director please enter their details below.
                    </Txt>
                  </FlexContainer>
                </Checkbox>
              </ContentBlock>
              {!dataState.isDirector ? (
                <>
                  <Txt regular>
                    Ensure the details of the director are correct for tenancy
                    agreement delivery and signing.
                  </Txt>
                  <LabeledInput
                    label="Title"
                    name="director.title"
                    onChange={handleChange}
                    value={dataState.director.title}
                    disabled={isLoading}
                    options={userTitleOptions}
                    required
                    select
                    noMargin
                  />
                  <LabeledInput
                    {...firstNameField}
                    name="director.firstName"
                    label="First Name"
                    onChange={handleChange}
                    value={dataState.director.firstName}
                    disabled={isLoading}
                    noMargin
                  />
                  <LabeledInput
                    {...lastNameField}
                    name="director.lastName"
                    label="Last Name"
                    onChange={handleChange}
                    value={dataState.director.lastName}
                    disabled={isLoading}
                    noMargin
                  />
                  <LabeledInput
                    {...phoneField}
                    name="director.mobileNumber"
                    label="Mobile number"
                    onChange={handleChange}
                    value={dataState.director.mobileNumber}
                    disabled={isLoading}
                    noMargin
                  />
                  <LabeledInput
                    label="Email address"
                    type="email"
                    name="director.emailAddress"
                    onChange={handleChange}
                    value={dataState.director.emailAddress}
                    disabled={isLoading}
                    required
                    noMargin
                  />
                </>
              ) : null}
            </>
          ) : null}
          {/*<FlexContainer column gapHalf>*/}
          {/*  <FlexContainer column gapQuad>*/}
          {/*    <View>*/}
          {/*      <Txt xx bold>*/}
          {/*        Correspondence address*/}
          {/*      </Txt>*/}
          {/*    </View>*/}
          {/*  </FlexContainer>*/}
          {/*  <InputAddress*/}
          {/*    name="address"*/}
          {/*    value={dataState.address}*/}
          {/*    onChange={handleChange}*/}
          {/*    disabled={isLoading}*/}
          {/*    fullWidth*/}
          {/*    useButton*/}
          {/*    required*/}
          {/*  />*/}
          {/*</FlexContainer>*/}
          <FlexContainer column gap>
            <FlexContainer column gapQuad>
              <View>
                <Txt xx bold>
                  Photo identification
                </Txt>
              </View>
              <View>
                <Txt regular>
                  This image can be a passport, drivers license or provisional
                  license. It will be reviewed by one of our team within 24
                  hours.
                </Txt>
              </View>
            </FlexContainer>
            <InputImages
              name="idImageData"
              value={dataState.idImageData}
              onChange={handleChange}
              disabled={isLoading}
              reverse
              resize={picturesMaxSize.id}
              required
            />
          </FlexContainer>
          {error ? <ErrorMessage small description={error} /> : null}
          <FlexContainer>
            <Button flex type="submit" big success disabled={isLoading}>
              {isLoading ? (
                <Spinner />
              ) : (
                <FlexContainer spaceBetween alignCenter flex>
                  <Txt xx>Submit</Txt>
                  <Txt>🎉</Txt>
                </FlexContainer>
              )}
            </Button>
          </FlexContainer>
        </FlexContainer>
      </form>
    </PartialWidthContainer>
  );
};

LandlordVerificationScreen.displayName = 'LandlordVerificationScreen';

export default observer(LandlordVerificationScreen);
